import styled from 'styled-components';
import {
  Button,
  Checkbox,
  Col,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Table,
  TableColumnsType,
  Tag,
  Typography,
  Upload,
  UploadProps,
} from 'antd';

import React, { useState } from 'react';
import { BiPencil, BiPlus, BiTrash } from 'react-icons/bi';
import { InboxOutlined } from '@ant-design/icons';
import {
  useCreateUserMutation,
  useDeleteUserMutation,
  useGetUsersQuery,
  useUpdateUserMutation,
} from '../../../redux/slices/users';
import { IUser } from 'models/auth';
import { config } from 'services/config';

const Wrapper = styled.div`
  height: calc(100vh - 300px);
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow-y: scroll;
`;
const EMPTY_PASS = '********';
const Users = () => {
  const { data, isLoading } = useGetUsersQuery('', {});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<IUser | undefined>(undefined);

  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();

  const columns: TableColumnsType<IUser> = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (value, _) => <Typography.Text strong>{value}</Typography.Text>,
    },
    {
      title: 'Name',
      key: 'name',
      render: (text, record) => (
        <Flex>
          <Typography.Text>{`${record.firstname} ${record.lastname}`}</Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (value, _) => <a href={`mailto:${value}`}>{value}</a>,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (text, _) =>
        text === 'admin' ? <Tag color={'red'}>Admin</Tag> : <Tag color={'blue'}>Client</Tag>,
    },

    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: (value, _) => <Tag color={value ? 'green' : 'red'}>{value ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <Flex>
          <Button
            data-testid="edit-user-button"
            type={'default'}
            size={'small'}
            icon={<BiPencil />}
            onClick={() => {
              setEditing(record);
              form.setFieldsValue(record);
              form.setFieldValue('password', EMPTY_PASS);
              setIsModalOpen(true);
            }}
          ></Button>
          <Popconfirm
            title={'Are you sure?'}
            onConfirm={() => {
              deleteUser(record.id);
            }}
          >
            <Button
              data-testid="delete-user-button"
              type={'default'}
              size={'small'}
              danger
              icon={<BiTrash />}
              style={{ marginLeft: 10 }}
            ></Button>
          </Popconfirm>
        </Flex>
      ),
    },
  ];

  const handleCreate = async (values: any) => {
    if (editing) {
      if (values.password === EMPTY_PASS) {
        delete values.password;
      }
      await updateUser({ ...editing, ...values });
    } else {
      await createUser({ ...values });
    }
    setEditing(undefined);
    form.resetFields();
    setIsModalOpen(false);
  };

  const { Dragger } = Upload;

  const draggerProps: UploadProps = {
    name: 'file',
    multiple: false,
    action: `${config.BASE_URL}/users/upload`,
    headers: { Authorization: `Bearer ${localStorage.getItem('AccessToken')}` },
    onChange(info) {
      const { status } = info.file;
      if (status === 'done') {
        message.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === 'error') {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
    onDrop: _e => undefined,
    style: { height: 200 },
  };

  return (
    <Wrapper>
      <Flex justify={'flex-end'} style={{ paddingBottom: 20 }}>
        <Button type={'primary'} icon={<BiPlus />} onClick={() => setIsModalOpen(true)}>
          New User
        </Button>
      </Flex>
      <Modal
        open={isModalOpen}
        footer={<></>}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();

          setEditing(undefined);
        }}
        width={1200}
      >
        <Typography.Title level={3}>{editing ? 'Edit User' : 'New User'}</Typography.Title>
        <Row gutter={24}>
          <Col span={14}>
            <Form form={form} onFinish={handleCreate} layout={'vertical'}>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label={'Username'} name={'username'} required>
                    <Input disabled={!!editing} placeholder="Username" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={'Password'} name={'password'} required>
                    <Input.Password placeholder="Password" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={6}>
                  <Form.Item label={'Role'} name={'role'} required>
                    <Select placeholder="Role" data-testid="role-select-input">
                      <Select.Option value={'admin'}>Admin</Select.Option>
                      <Select.Option value={'client'}>Client</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={18}>
                  <Form.Item
                    label={'Email'}
                    name={'email'}
                    required
                    rules={[
                      {
                        type: 'email',
                        message: 'Please enter a valid email',
                      },
                    ]}
                  >
                    <Input placeholder="Email" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={10}>
                  <Form.Item label={'First Name'} name={'firstname'}>
                    <Input placeholder="First Name" />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item label={'Last Name'} name={'lastname'}>
                    <Input placeholder="Last Name" />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    label={'Active'}
                    name={'active'}
                    valuePropName={'checked'}
                    initialValue={true}
                  >
                    <Checkbox data-testid="active-user-checkbox" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button
                  type={'primary'}
                  htmlType={'submit'}
                  // loading={isCreating || isUpdating}
                >
                  {editing ? 'Update User' : 'Create New User'}
                </Button>
              </Form.Item>
            </Form>
          </Col>
          <Col span={10} style={{ borderLeft: 'solid 1px #d9d9d9' }}>
            {editing ? (
              <Flex justify={'center'} align={'center'} style={{ height: 300, color: '#aaaaaa' }}>
                {' '}
                Bulk upload not supported in Editing Mode
              </Flex>
            ) : (
              <Dragger {...draggerProps}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Bulk import users from a CSV file</p>
                <p className="ant-upload-hint">Click or drag file to this area to upload</p>
              </Dragger>
            )}
          </Col>
        </Row>
      </Modal>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={isLoading}
        pagination={{ pageSize: 5 }}
      />
    </Wrapper>
  );
};
export default Users;
